'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import WardStewardOnboarding from './components/WardStewardOnboarding';
import WidgetTestHarness from './components/WidgetTestHarness';
import GuidedReconstructionWorkflow from './components/GuidedReconstructionWorkflow';

// ─── TYPES ────────────────────────────────────────────────────────────────────

type SubmissionType = 'resource' | 'disparity' | 'context';
type SubmissionStatus = 'pending' | 'approved' | 'rejected' | 'published';

interface Submission {
  id: string;
  type: SubmissionType;
  city: string;
  ward: string;
  title: string;
  content: string;
  submittedBy: string;
  submittedAt: string;
  status: SubmissionStatus;
  tags: string[];
}

interface CityMetrics {
  city: string;
  resources: number;
  disparities: number;
  context: number;
  pending: number;
  caretakers: number;
  lastUpdated: string;
}

// ─── MOCK DATA ────────────────────────────────────────────────────────────────

const MOCK_SUBMISSIONS: Submission[] = [
  { id: 's1', type: 'resource', city: 'Chicago', ward: 'South Side', title: 'Masjid Al-Faatir Food Pantry', content: 'Weekly food pantry every Saturday 10am-2pm. Open to all community members regardless of faith. Located at 7351 S Stony Island Ave. Contact: 773-555-0142.', submittedBy: 'caretaker_chicago_01', submittedAt: '2026-03-19T14:30:00Z', status: 'pending', tags: ['food', 'masjid', 'weekly'] },
  { id: 's2', type: 'disparity', city: 'Chicago', ward: 'Englewood', title: 'Healthcare Desert — No Primary Care Within 2 Miles', content: 'Englewood has no primary care clinic within a 2-mile radius. Nearest urgent care is 3.4 miles away. Residents rely on emergency rooms for basic care. Muslim community members particularly affected due to language barriers at distant facilities.', submittedBy: 'caretaker_chicago_02', submittedAt: '2026-03-18T09:15:00Z', status: 'pending', tags: ['healthcare', 'disparity', 'access'] },
  { id: 's3', type: 'context', city: 'Atlanta', ward: 'West End', title: 'Growing Somali Community — Cultural Context', content: 'West End has seen significant growth in the Somali Muslim community over the past 5 years. Key needs: halal food access, Somali-speaking social workers, and youth programming that bridges cultural identity with American context.', submittedBy: 'caretaker_atlanta_01', submittedAt: '2026-03-17T16:45:00Z', status: 'pending', tags: ['somali', 'cultural', 'youth'] },
  { id: 's4', type: 'resource', city: 'Detroit', ward: 'Dearborn', title: 'ACCESS — Arab Community Center', content: 'Comprehensive social services for Arab and Muslim community. Mental health, legal aid, employment services, food assistance. Arabic and English speaking staff. 6451 Schaefer Rd, Dearborn.', submittedBy: 'caretaker_detroit_01', submittedAt: '2026-03-16T11:00:00Z', status: 'approved', tags: ['social-services', 'arabic', 'comprehensive'] },
  { id: 's5', type: 'disparity', city: 'Houston', ward: 'Mahatma Gandhi District', title: 'Youth Unemployment — 35% Among Muslim Youth 18-24', content: 'Survey of 200 Muslim youth in the district found 35% unemployment rate. Contributing factors: lack of halal-friendly workplaces, discrimination, limited vocational training aligned with Islamic values.', submittedBy: 'caretaker_houston_01', submittedAt: '2026-03-15T13:20:00Z', status: 'pending', tags: ['youth', 'employment', 'survey'] },
  { id: 's6', type: 'resource', city: 'New York', ward: 'Flatbush', title: 'Muslim Community Network — Emergency Fund', content: 'Emergency financial assistance for Muslim families in crisis. Up to $500 per household per year. Applications through local masjid. Contact MCN at 718-555-0199.', submittedBy: 'caretaker_ny_01', submittedAt: '2026-03-14T08:30:00Z', status: 'published', tags: ['emergency', 'financial', 'assistance'] },
];

const CITY_METRICS: CityMetrics[] = [
  { city: 'Chicago', resources: 12, disparities: 8, context: 5, pending: 3, caretakers: 24, lastUpdated: '2026-03-19' },
  { city: 'Atlanta', resources: 7, disparities: 4, context: 6, pending: 2, caretakers: 11, lastUpdated: '2026-03-17' },
  { city: 'Detroit', resources: 15, disparities: 6, context: 4, pending: 1, caretakers: 18, lastUpdated: '2026-03-16' },
  { city: 'Houston', resources: 9, disparities: 7, context: 3, pending: 2, caretakers: 14, lastUpdated: '2026-03-15' },
  { city: 'New York', resources: 22, disparities: 11, context: 8, pending: 0, caretakers: 31, lastUpdated: '2026-03-14' },
  { city: 'Los Angeles', resources: 6, disparities: 3, context: 2, pending: 4, caretakers: 9, lastUpdated: '2026-03-12' },
];

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: SubmissionStatus }) {
  const config = {
    pending: { color: '#c9a84c', bg: 'rgba(201,168,76,0.12)', label: 'Pending Review' },
    approved: { color: '#2d8a87', bg: 'rgba(45,138,135,0.12)', label: 'Approved' },
    rejected: { color: '#e05a5a', bg: 'rgba(180,80,80,0.12)', label: 'Rejected' },
    published: { color: '#2d7a47', bg: 'rgba(45,122,71,0.12)', label: 'Published' },
  }[status];
  return (
    <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '3px 8px', borderRadius: 10, background: config.bg, border: `1px solid ${config.color}40`, color: config.color, textTransform: 'uppercase', letterSpacing: '.08em' }}>
      {config.label}
    </span>
  );
}

function TypeBadge({ type }: { type: SubmissionType }) {
  const config = {
    resource: { color: '#2d7a47', icon: '⬡', label: 'Resource' },
    disparity: { color: '#e05a5a', icon: '⚠', label: 'Disparity' },
    context: { color: '#8a8ab8', icon: '◎', label: 'Context' },
  }[type];
  return (
    <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '3px 8px', borderRadius: 4, background: `${config.color}10`, border: `1px solid ${config.color}30`, color: config.color, textTransform: 'uppercase', letterSpacing: '.06em', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {config.icon} {config.label}
    </span>
  );
}

// ─── SUBMISSION CARD ──────────────────────────────────────────────────────────

function SubmissionCard({ sub, onAction }: { sub: Submission; onAction: (id: string, action: SubmissionStatus) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState('');

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${sub.status === 'pending' ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.06)'}`, borderLeft: `3px solid ${sub.status === 'pending' ? '#c9a84c' : sub.status === 'approved' ? '#2d8a87' : sub.status === 'published' ? '#2d7a47' : '#e05a5a'}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      <div style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }} onClick={() => setExpanded(v => !v)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <TypeBadge type={sub.type} />
            <StatusBadge status={sub.status} />
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em' }}>{sub.city} · {sub.ward}</span>
          </div>
          <p style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700, marginBottom: 2 }}>{sub.title}</p>
          <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10 }}>
            by {sub.submittedBy} · {new Date(sub.submittedAt).toLocaleDateString()}
          </p>
        </div>
        <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 12, flexShrink: 0, marginTop: 2 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11, lineHeight: 1.7, marginTop: 12, marginBottom: 10 }}>{sub.content}</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {sub.tags.map(tag => (
              <span key={tag} style={{ fontFamily: 'monospace', fontSize: 7, padding: '2px 7px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{tag}</span>
            ))}
          </div>
          {sub.status === 'pending' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Add a steward note (optional)..." rows={2} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => onAction(sub.id, 'approved')} style={{ flex: 1, padding: '8px', background: 'rgba(45,138,135,0.12)', border: '1px solid rgba(45,138,135,0.4)', borderRadius: 6, color: '#2d8a87', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
                  ✓ Approve
                </button>
                <button onClick={() => onAction(sub.id, 'published')} style={{ flex: 1, padding: '8px', background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.4)', borderRadius: 6, color: '#2d7a47', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
                  ⬡ Approve & Publish
                </button>
                <button onClick={() => onAction(sub.id, 'rejected')} style={{ padding: '8px 14px', background: 'rgba(180,80,80,0.08)', border: '1px solid rgba(180,80,80,0.3)', borderRadius: 6, color: '#e05a5a', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
                  ✕ Reject
                </button>
              </div>
            </div>
          )}
          {sub.status === 'approved' && (
            <button onClick={() => onAction(sub.id, 'published')} style={{ padding: '8px 16px', background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.4)', borderRadius: 6, color: '#2d7a47', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
              ⬡ Publish to MRG Collection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CITY METRICS CARD ────────────────────────────────────────────────────────

function CityMetricsCard({ metrics }: { metrics: CityMetrics }) {
  const total = metrics.resources + metrics.disparities + metrics.context;
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{metrics.city}</p>
        {metrics.pending > 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '2px 7px', borderRadius: 10, background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '.08em' }}>
            {metrics.pending} pending
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
        {[
          { label: 'Resources', value: metrics.resources, color: '#2d7a47' },
          { label: 'Disparities', value: metrics.disparities, color: '#e05a5a' },
          { label: 'Context', value: metrics.context, color: '#8a8ab8' },
        ].map(m => (
          <div key={m.label} style={{ textAlign: 'center', padding: '6px', background: `${m.color}08`, border: `1px solid ${m.color}20`, borderRadius: 6 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 900, color: m.color }}>{m.value}</p>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: `${m.color}70`, textTransform: 'uppercase', letterSpacing: '.06em' }}>{m.label}</p>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>
          {metrics.caretakers} caretakers · {total} total entries
        </span>
        <Link href={`/mrg-embed?city=${metrics.city.toLowerCase()}`} style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)', textDecoration: 'none', padding: '3px 8px', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 4 }}>
          View →
        </Link>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function WardStewardPage() {
  const [submissions, setSubmissions] = useState<Submission[]>(MOCK_SUBMISSIONS);
  const [activeTab, setActiveTab] = useState<'pending' | 'all' | 'metrics' | 'published'>('pending');
  const [filterCity, setFilterCity] = useState('all');
  const [filterType, setFilterType] = useState<'all' | SubmissionType>('all');

  const handleAction = (id: string, action: SubmissionStatus) => {
    setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: action } : s));
  };

  const cities = ['all', ...Array.from(new Set(submissions.map(s => s.city)))];

  const filtered = submissions.filter(s => {
    if (activeTab === 'pending') return s.status === 'pending';
    if (activeTab === 'published') return s.status === 'published';
    if (filterCity !== 'all' && s.city !== filterCity) return false;
    if (filterType !== 'all' && s.type !== filterType) return false;
    return true;
  });

  const pendingCount = submissions.filter(s => s.status === 'pending').length;
  const publishedCount = submissions.filter(s => s.status === 'published').length;
  const totalCities = new Set(submissions.map(s => s.city)).size;

  return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(80px, 15vw, 96px) clamp(12px, 4vw, 24px) clamp(48px, 8vw, 64px)' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.5)', marginBottom: 6 }}>⬡ Ward Steward Dashboard</p>
          <h1 style={{ fontFamily: 'monospace', fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.02em', color: '#F5F0E8', marginBottom: 6 }}>
            Ward Steward
          </h1>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.6 }}>
            Review caretaker submissions, approve resources and disparities for the MRG collection, and monitor community impact by city and ward.
          </p>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 28 }}>
          {[
            { label: 'Pending Review', value: pendingCount, color: '#c9a84c', icon: '⏳' },
            { label: 'Published', value: publishedCount, color: '#2d7a47', icon: '⬡' },
            { label: 'Cities Active', value: totalCities, color: '#2d8a87', icon: '◎' },
            { label: 'Total Submissions', value: submissions.length, color: '#8a8ab8', icon: '✦' },
          ].map(stat => (
            <div key={stat.label} style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}20`, borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 900, color: stat.color }}>{stat.value}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${stat.color}70`, textTransform: 'uppercase', letterSpacing: '.08em' }}>{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: 4, width: 'fit-content' }}>
          {([
            { id: 'pending', label: `Pending (${pendingCount})`, icon: '⏳' },
            { id: 'all', label: 'All Submissions', icon: '◎' },
            { id: 'published', label: `Published (${publishedCount})`, icon: '⬡' },
            { id: 'metrics', label: 'City Metrics', icon: '✦' },
          ] as const).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '8px 14px', background: activeTab === tab.id ? 'rgba(138,138,184,0.15)' : 'transparent', border: `1px solid ${activeTab === tab.id ? 'rgba(138,138,184,0.4)' : 'transparent'}`, borderRadius: 6, color: activeTab === tab.id ? '#8a8ab8' : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Filters for 'all' tab */}
        {activeTab === 'all' && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <select value={filterCity} onChange={e => setFilterCity(e.target.value)} style={{ background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', cursor: 'pointer' }}>
              {cities.map(c => <option key={c} value={c}>{c === 'all' ? 'All Cities' : c}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value as any)} style={{ background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', cursor: 'pointer' }}>
              <option value="all">All Types</option>
              <option value="resource">Resources</option>
              <option value="disparity">Disparities</option>
              <option value="context">Context</option>
            </select>
          </div>
        )}

        {/* Content */}
        {activeTab === 'metrics' ? (
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 16 }}>
              MRG Collection Metrics by City
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {CITY_METRICS.map(m => <CityMetricsCard key={m.city} metrics={m} />)}
            </div>
          </div>
        ) : (
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 14 }}>
              {filtered.length} submission{filtered.length !== 1 ? 's' : ''}
              {activeTab === 'pending' && pendingCount > 0 && ' — click to expand and review'}
            </p>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', background: 'rgba(255,255,255,0.01)', border: '1px dashed rgba(255,255,255,0.06)', borderRadius: 10 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                  {activeTab === 'pending' ? 'No pending submissions — all clear.' : 'No submissions match these filters.'}
                </p>
              </div>
            ) : (
              filtered.map(sub => <SubmissionCard key={sub.id} sub={sub} onAction={handleAction} />)
            )}
          </div>
        )}

        {/* Footer links */}
        <div style={{ marginTop: 32, padding: '16px 20px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em' }}>
            Ward Steward Dashboard · The Haal Project · haalproject.org
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <Link href="/mrg-embed/collection" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 4 }}>MRG Collection →</Link>
            <Link href="/user-dashboard" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.6)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 4 }}>My Garden →</Link>
          </div>
        </div>
      </main>

      {/* ── WARD STEWARD ONBOARDING WALKTHROUGH ── */}
      <WardStewardOnboarding />

      {/* ── WIDGET TEST HARNESS ── */}
      <WidgetTestHarness />

      {/* ── GUIDED RECONSTRUCTION WORKFLOW ── */}
      <GuidedReconstructionWorkflow />

      <Footer />
    </div>
  );
}
